import DSButton from '@/components/design-system/Button';
import React, { useEffect, useRef, useState } from 'react';
import OtpInput from 'react-otp-input';
import Countdown from 'react-countdown';
import { useMutation } from '@tanstack/react-query';
import { resendOtpQueryFn, verifyOtpQueryFn } from '@/queries/auth';
import useAuth from '@/contexts/AuthContext';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import toast from 'react-hot-toast';
import { Constants } from '@/helpers/Constants';
import i18next from 'i18n';
import isNil from 'lodash.isnil';
import DSIcon from '@/components/design-system/Icon';

const OTP_LENGTH = 6;

const isComplete = (code: string) => code.length === OTP_LENGTH && /^\d+$/.test(code);

/**
 * Writes `digits` into the boxes starting at `index`.
 *
 * react-otp-input tracks the focused box in its own state and writes every
 * change there, which only holds while a person types one key per render.
 * Safari filling a code from a message sets all six boxes in a single batch, so
 * that state is a render behind for boxes two to six and every digit lands on
 * top of the first. Writing at the index of the box that actually changed is
 * what makes the autofill land intact; it also absorbs the browsers that drop
 * the whole code into one box.
 */
const writeDigits = (current: string, index: number, digits: string) =>
    (current.slice(0, index) + digits + current.slice(index + digits.length)).slice(0, OTP_LENGTH);

const OtpForm = ({ fourDigits, buttonText = i18next.t('general.sign-in'), external = false, onSubmit, loading = null, onLoginSuccess }: { fourDigits: string; buttonText?: string; external?: boolean; onSubmit?: (otp: string) => void, loading?: boolean | null; onLoginSuccess?: () => void | null; }) => {
    const [isResend, setIsResend] = useState<boolean>(true);
    const [resendBlockedUntil, setResendBlockedUntil] = useState<number>(() => Date.now() + Constants.Auth.TIME_FOR_OTP_RESEND);
    const [otp, setOtp] = useState<string>('');
    const { setUser, updateRefreshToken } = useAuth();
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    /** The code the effect below already fired, so a re-render cannot fire it twice. */
    const autoSubmittedOtp = useRef<string | null>(null);

    const resendOtpMutation = useMutation({
        mutationFn: resendOtpQueryFn,
        onError: async () => {
        }
    });

    const verifyOtpMutation = useMutation({
        mutationFn: verifyOtpQueryFn,
        onSuccess: async (response) => {
            if (response && response.user) {
                if (response && response.refresh_token) {
                    updateRefreshToken(response.refresh_token);
                }

                setUser(response.user);

                if (onLoginSuccess) {
                    onLoginSuccess();
                }
            }
        },
        onError: async (error) => {
            if (ErrorHandler.Auth.badOtp(await ErrorHandler.transformError(error))) {
                toast.error(i18next.t('error-notification.invalid-otp-code'));
            }
        }
    });

    const submitting = !!(verifyOtpMutation.isPending || loading);

    const submitOtp = (code: string) => {
        if (!isComplete(code) || submitting) {
            return;
        }

        if (external && onSubmit) {
            onSubmit(code);
        } else {
            verifyOtpMutation.mutate(code);
        }
    };

    /*
     * A filled code submits itself, which is the whole point of the autofill:
     * Safari drops the six digits in and the form goes without a second tap.
     * Typing the sixth digit by hand does the same, as the button would be the
     * only thing left to press anyway.
     *
     * Only `otp` belongs in the deps. Re-running this on every render would be
     * harmless (the ref guards it), but the intent is one shot per code, and
     * clearing the ref while the code is short is what lets the same code go
     * again after a rejection.
     */
    useEffect(() => {
        if (!isComplete(otp)) {
            autoSubmittedOtp.current = null;
            return;
        }

        if (autoSubmittedOtp.current === otp) {
            return;
        }

        autoSubmittedOtp.current = otp;
        submitOtp(otp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp]);

    const focusBox = (index: number) => {
        const box = inputRefs.current[Math.min(Math.max(index, 0), OTP_LENGTH - 1)];
        box?.focus();
        box?.select();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const raw = event.target.value;
        const digits = raw.replace(/\D/g, '');

        if (digits === '') {
            if (raw !== '') {
                // `tel` inputs take letters on a desktop keyboard. State has not
                // moved, so React will not redraw the box; put it back by hand.
                const box = event.target;
                box.value = otp[index] ?? '';
                return;
            }

            // Android reports a backspace here rather than in keydown.
            setOtp((current) => current.slice(0, index) + current.slice(index + 1));
            focusBox(index - 1);
            return;
        }

        setOtp((current) => writeDigits(current, index, digits));
        focusBox(index + digits.length);
    };

    return (
        /*
         * A real form, so Enter submits the way it does in every other field on
         * the page. The library leaves Enter alone in its keydown handler, and
         * the browser skips implicit submission while the submit button is
         * disabled, so an unfinished code does nothing.
         */
        <form
            className="flex flex-col items-center"
            onSubmit={(event) => {
                event.preventDefault();
                submitOtp(otp);
            }}
        >
            <span className="text-body-400 text-center mb-4">
                {i18next.t('success-notification.email-verification-sent', { fourDigits })}
            </span>
            <OtpInput
                value={otp}
                onChange={setOtp}
                shouldAutoFocus
                numInputs={OTP_LENGTH}
                inputType="tel"
                renderSeparator={(index: number) => {
                    return index === 2 ? <span className="text-h1 inline-block w-[46px] text-surface-300">-</span> : '';
                }}
                renderInput={(props, index) => {
                    return (
                        <input
                            {...props}
                            // The library hardcodes `off`. This is the attribute Safari
                            // and Chrome look for before offering the code from a message.
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            ref={(element) => {
                                inputRefs.current[index] = element;
                                // The library still needs its own handle for paste and
                                // arrow-key navigation. `props` here is the library's
                                // input props, not a component's, hence the disable.
                                // eslint-disable-next-line react/prop-types
                                props.ref(element);
                            }}
                            onChange={(event) => handleChange(event, index)}
                            placeholder={i18next.t('general.otp-placeholder')}
                            className="!w-[46px] !h-[62px] text-h1 placeholder:text-surface-300 mx-1 inline-block border-[1px] border-surface-300 rounded-none"
                        />
                    );
                }}
            />
            <span className="text-body-600 text-center mt-6 ">{i18next.t('common:general.didnt-get-code')}</span>
            <button
                // Without this it defaults to `submit` inside the form above and
                // becomes the button Enter reaches first.
                type="button"
                className="text-body-600 text-center mt-1"
                onClick={() => {
                    if (isResend) {
                        return;
                    }
                    setResendBlockedUntil(Date.now() + Constants.Auth.TIME_FOR_OTP_RESEND);
                    setIsResend(true);
                    resendOtpMutation.mutate();
                }}
            >
                <span className="text-body-600 ml-1 text-center text-ink-400 underline block mt-1">
                    {!isResend ? i18next.t('common:general.resend') :
                        (
                            <div className="mt-2">
                                {!isNil(resendBlockedUntil) && <Countdown
                                    date={resendBlockedUntil}
                                    onComplete={() => setIsResend(false)}
                                    renderer={({ seconds }) => {
                                        return i18next.t('general.can-request-new-otp', { seconds });
                                    }}
                                />}
                            </div>
                        )}
                </span>
            </button>
            <DSButton
                icon={<DSIcon name="arrow-right" size={20} />}
                fullWidth
                type="submit"
                loading={submitting}
                disabled={submitting || !isComplete(otp)}
                className="mt-8"
            >
                {buttonText}
            </DSButton>
        </form>
    );
};

export default OtpForm;
